import ParameterMapping, {
    decodeTransportModeParameter,
    ExtendedParameterName,
    ParameterMappingProps,
} from 'views/CalculateEmissions/RFQ/components/ParameterMapping'
import {
    ParameterName,
    parseParameterName,
    PropertyMapping,
    TransportMode,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    AutoFillEntryWithUpdate,
    LegAutoFillWithUpdate,
    OptionalTransportModeMapping,
    TRANSPORT_MODES_PER_GROUP_PROPERTY,
} from 'views/CalculateEmissions/RFQ/utils/types'
import { objectHasKey } from 'views/CalculateEmissions/RFQ/utils/utils'

type GroupPropertiesMap = typeof TRANSPORT_MODES_PER_GROUP_PROPERTY

type GroupedParameterMappingProps<
    T,
    X extends keyof GroupPropertiesMap,
    Y extends keyof GroupPropertiesMap[X] & string,
> = Pick<ParameterMappingProps<T>, 'state' | 'mappingType' | 'outputValues' | 'formatOutput'> & {
    group: X
    parameter: Y
    transportModeMapping: OptionalTransportModeMapping
    onUpdate: (
        transportModes: TransportMode[],
        parameter: ParameterName,
        mapping: PropertyMapping<unknown> | undefined,
    ) => void
    autoFill: LegAutoFillWithUpdate['transportMode']
    error?: string
    allowedRowIndexes?: { [k in TransportMode]?: number[] }
}

const GroupedParameterMapping = <
    T,
    X extends keyof GroupPropertiesMap,
    Y extends keyof GroupPropertiesMap[X] & string,
>({
    state,
    group,
    parameter,
    mappingType,
    outputValues,
    formatOutput,
    transportModeMapping,
    onUpdate,
    error,
    autoFill,
    allowedRowIndexes,
}: GroupedParameterMappingProps<T, X, Y>) => {
    // Transport mode mapping data and options might be out of sync at times due to the
    // fact they are synced via multiple `useEffect`. Return nothing in those cases.
    if (transportModeMapping?.mode?.source !== 'column') {
        return <></>
    }
    // TS compiler isn't smart enough to resolve types here, but we make sure this is true
    const transportModes = TRANSPORT_MODES_PER_GROUP_PROPERTY[group][parameter] as TransportMode[]
    const fullAllowedRowIndexes = transportModes.flatMap((t) => allowedRowIndexes?.[t] ?? [])
    if (fullAllowedRowIndexes.length === 0) {
        return <></>
    }

    const firstTransportMode = transportModes[0]
    const extendedParameterName = `${firstTransportMode}_${parameter}` as ExtendedParameterName
    // TODO remove assertions here and below. Type improvements are required but currently
    // we guarantee this: ExtendedParameterName is in sync with our parameters
    const firstTransportModeData = transportModeMapping.data?.[firstTransportMode]
    const firstPropertyMapping = objectHasKey(firstTransportModeData, parameter)
        ? (firstTransportModeData[parameter] as PropertyMapping<T> | undefined)
        : undefined

    const firstTransportModeAutoFill = autoFill[firstTransportMode]
    const firstAutoFill = objectHasKey(firstTransportModeAutoFill, parameter)
        ? (firstTransportModeAutoFill[parameter] as AutoFillEntryWithUpdate | undefined)
        : undefined

    const applyUpdateToAllTransportModes = (
        parameterName: ExtendedParameterName,
        data: PropertyMapping<T> | undefined,
    ): void => {
        const parameterInfo = decodeTransportModeParameter(parameterName)
        const parameter = parseParameterName(parameterInfo.parameter).unwrap()
        onUpdate(transportModes, parameter, data)
    }

    return (
        <ParameterMapping<T>
            key={`${group}-${parameter}`}
            state={state}
            useTruncatedLabel={true}
            mappingType={mappingType}
            outputValues={outputValues}
            formatOutput={formatOutput}
            onUpdate={applyUpdateToAllTransportModes}
            legNumber={state.currentLeg}
            allowedRowIndexes={fullAllowedRowIndexes}
            error={error}
            parameter={extendedParameterName}
            propertyMapping={firstPropertyMapping}
            autoFill={firstAutoFill}
        />
    )
}

export default GroupedParameterMapping
